import { normalize, schema } from 'normalizr';
import { isArray, isBoolean, isNumber, isObject } from 'util';

import * as OffersActions from '../actions/offers';
import * as QueryActions from '../actions/query';
import * as SelectionActions from '../actions/selection';
import * as TabsActions from '../../../xena-tabs/state-management/actions/tabs';

import { Group } from '../../shared/models/groups/groups.model';
import { convertDayPriceMatrix } from '../../shared/models/day-price-matrix/convert-day-price-matrix';
import { ConvertedDayPriceMatrix } from '../../shared/models/day-price-matrix/converted-day-price-matrix.model';
import { InitialState as InitialTabState } from '../../../xena-tabs/state-management/reducers/tabs';


export interface StateObject {
	result: Array<string>;
	entities: {
		offers: { [s: string]: Group };
	};
	count: number;
	dayPriceMatrix: Array<ConvertedDayPriceMatrix>;
	showMatrix: boolean;
	loading: boolean;
	tab: number;
	selected: boolean;
}

export type State = Array<StateObject>;

export const initialState: State = [{
	result: [],
	entities: {offers: {}},
	count: 0,
	dayPriceMatrix: [],
	showMatrix: false,
	loading: false,
	tab: InitialTabState[0].id,
	selected: InitialTabState[0].selected
}];

const offersSchema = new schema.Entity('offers');

export function reducer(state: State = initialState, action: OffersActions.All | TabsActions.All | SelectionActions.All | QueryActions.All): State {
	switch (action.type) {
		case OffersActions.INIT_OFFER_TABS:
			return action.payload.map((tab, index) => ({
				result: state[index] && isArray(state[index].result) ? state[index].result : initialState[0].result,
				entities: state[index] && isObject(state[index].entities) ? state[index].entities : initialState[0].entities,
				count: state[index] && isNumber(state[index].count) ? state[index].count : initialState[0].count,
				dayPriceMatrix: state[index] && isArray(state[index].dayPriceMatrix) ? state[index].dayPriceMatrix : initialState[0].dayPriceMatrix,
				showMatrix: state[index] && isBoolean(state[index].showMatrix) ? state[index].showMatrix : initialState[0].showMatrix,
				loading: state[index] && isBoolean(state[index].loading) ? state[index].loading : initialState[0].loading,
				tab: tab.id,
				selected: tab.selected
			}));

		case TabsActions.ADD_TAB:
			return state.concat({result: [], entities: {offers: {}}, dayPriceMatrix: [], showMatrix: false, count: 0, loading: false, tab: action.payload.id, selected: false});

		case TabsActions.SELECT_TAB:
			return state.map(stateObj => ({...stateObj, selected: stateObj.tab === action.payload.id}));

		case TabsActions.REMOVE_TAB:
			if (state.length === 1) {
				return state;
			}

			const tabToRemove = state.find(stateObj => stateObj.tab === action.payload.id);
			const newState = state.filter(stateObj => stateObj.tab !== action.payload.id);

			if (tabToRemove.selected) {
				newState[0].selected = true;
			}

			return newState;

		case SelectionActions.SELECT_REGION:
		case QueryActions.SUBMIT_QUERY:
		case QueryActions.RESET_QUERY:
		case OffersActions.LOAD_OFFERS_ERROR:
			return state.map(stateObj => ({...stateObj, ...(stateObj.selected ? {result: [], entities: {offers: []}, count: 0, dayPriceMatrix: [], showMatrix: false, loading: false} : {})}));

		case SelectionActions.SELECT_GROUP:
			return state.map(stateObj => ({...stateObj, ...(stateObj.selected ? {result: [], entities: {offers: []}, count: 0, dayPriceMatrix: [], showMatrix: false, loading: true} : {})}));

		case OffersActions.LOAD_OFFERS_SUCCESS:
			return state.map(stateObj => Object.assign({}, stateObj, (stateObj.selected ? {...normalize(action.payload.data, [offersSchema]), count: action.payload.count, dayPriceMatrix: convertDayPriceMatrix(action.payload.dayPriceMatrix, action.payload.data), loading: false} : {})));

		case OffersActions.SHOW_MATRIX:
			return state.map(stateObj => ({...stateObj, showMatrix: (stateObj.selected ? true : stateObj.showMatrix)}));

		case OffersActions.HIDE_MATRIX:
			return state.map(stateObj => ({...stateObj, showMatrix: (stateObj.selected ? false : stateObj.showMatrix)}));

		default:
			return state;
	}
}

/**
 * helper selector functions
 */
export const GetAll = (s: State): Array<Group> => s.filter(st => st.selected).map(st => st.result.map(result => st.entities.offers[result]))[0];
export const GetSelected = (s: State): StateObject => s.find(st => st.selected);
export const ShowMatrix = (s: State): boolean => s.find(st => st.selected).showMatrix;



// WEBPACK FOOTER //
// ./src/app/xena/core/state-management/reducers/offers.ts